/**
 * AdrFormTabs - shared ADR form UI.
 *
 * No tabs — everything is shown inline:
 * 1. Title input
 * 2. Compact metadata (status, impl, scope, people)
 * 3. BlockNote editors (context, decision, consequences) — main focus
 * 4. Relations + Comments (if existing ADR)
 *
 * Used by both AdrModal and AdrDetailSidebar.
 */

import { useTranslation } from 'react-i18next';
import type { AdrForm } from '../useAdrForm';
import type { AdrStatus } from '../../types';
import { AdrCommentTree } from './AdrCommentTree';
import { AdrRelationPicker } from './AdrRelationPicker';
import { AdrPersonPicker } from './AdrPersonPicker';
import { AdrBlockEditor } from './AdrBlockEditor';
import { useAdrActions } from '../../stores';

const STATUS_OPTIONS: { value: AdrStatus; color: string }[] = [
  { value: 'draft', color: '#9ca3af' },
  { value: 'proposed', color: '#f59e0b' },
  { value: 'accepted', color: '#10b981' },
  { value: 'deprecated', color: '#ef4444' },
  { value: 'superseded', color: '#f97316' },
];

const IMPL_STATUS_OPTIONS = ['pending', 'in_progress', 'done', 'abandoned'] as const;

interface AdrFormTabsProps {
  form: AdrForm;
  /** Horizontal padding class, e.g. "px-4" for modal, "px-3" for sidebar */
  px?: string;
  /** Whether to auto-focus the title input */
  autoFocusTitle?: boolean;
}

export function AdrFormTabs({ form, px = 'px-4', autoFocusTitle = false }: AdrFormTabsProps) {
  const { t } = useTranslation();
  const actions = useAdrActions();

  return (
    <>
      {/* Title */}
      <div className={`${px} pt-3`}>
        <input
          type="text"
          value={form.title}
          onChange={(e) => form.setTitle(e.target.value)}
          placeholder="ADR Title"
          className="w-full text-sm font-medium text-gray-900 placeholder-gray-400 bg-white border border-gray-200 rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          autoFocus={autoFocusTitle}
        />
      </div>

      {/* Compact metadata – single row of cycle-on-click chips */}
      <div className={`${px} py-1.5 flex flex-wrap items-center gap-1.5 border-b border-gray-100`}>
        {/* Status chip */}
        <button
          tabIndex={-1}
          onClick={() => {
            const i = STATUS_OPTIONS.findIndex(o => o.value === form.status);
            form.setStatus(STATUS_OPTIONS[(i + 1) % STATUS_OPTIONS.length].value);
          }}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded hover:bg-gray-100 transition-colors"
          title={t('adr.status.label')}
        >
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ backgroundColor: STATUS_OPTIONS.find(o => o.value === form.status)?.color }}
          />
          <span className="text-gray-500">{t(`adr.status.${form.status}` as any)}</span>
        </button>

        <span className="text-gray-200">·</span>

        {/* Impl status chip */}
        <button
          tabIndex={-1}
          onClick={() => {
            const i = IMPL_STATUS_OPTIONS.indexOf(form.implStatus);
            form.setImplStatus(IMPL_STATUS_OPTIONS[(i + 1) % IMPL_STATUS_OPTIONS.length]);
          }}
          className="px-1.5 py-0.5 text-[9px] text-gray-400 rounded hover:bg-gray-100 hover:text-gray-600 transition-colors"
          title={t('adr.implStatus.label')}
        >
          {t(`adr.implStatus.${form.implStatus}` as any)}
        </button>

        <span className="text-gray-200">·</span>

        {/* Scope chip */}
        <button
          tabIndex={-1}
          onClick={() => {
            const scopes = ['global', 'board', 'canvas'] as const;
            const i = scopes.indexOf(form.scopeType);
            form.setScopeType(scopes[(i + 1) % scopes.length]);
          }}
          className="px-1.5 py-0.5 text-[9px] text-gray-400 rounded hover:bg-gray-100 hover:text-gray-600 transition-colors"
          title={t('adr.scope')}
        >
          {t(`adr.scope${form.scopeType.charAt(0).toUpperCase() + form.scopeType.slice(1)}` as any)}
        </button>

      </div>

      {/* Scrollable content — editors + relations + comments */}
      <div className={`${px} py-3 overflow-y-auto flex-1 space-y-4`}>
        {/* Context */}
        <div>
          <span className="text-[10px] text-gray-400 font-medium block mb-0.5">{t('adr.context')}</span>
          <AdrBlockEditor
            key={`ctx-${form.editorKey}`}
            initialContent={form.context}
            onEditorReady={form.setContextEditor}
            onContentChange={form.onEditorChange}
            placeholder={t('adr.contextPlaceholder')}
          />
        </div>

        {/* Decision */}
        <div>
          <span className="text-[10px] text-gray-400 font-medium block mb-0.5">{t('adr.decision')}</span>
          <AdrBlockEditor
            key={`dec-${form.editorKey}`}
            initialContent={form.decision}
            onEditorReady={form.setDecisionEditor}
            onContentChange={form.onEditorChange}
            placeholder={t('adr.decisionPlaceholder')}
          />
        </div>

        {/* Consequences */}
        <div>
          <span className="text-[10px] text-gray-400 font-medium block mb-0.5">{t('adr.consequences')}</span>
          <AdrBlockEditor
            key={`cons-${form.editorKey}`}
            initialContent={form.consequences}
            onEditorReady={form.setConsequencesEditor}
            onContentChange={form.onEditorChange}
            placeholder={t('adr.consequencesPlaceholder')}
          />
        </div>

        {/* Relations */}
        {form.loadedAdrId && (
          <div className="border-t border-gray-100 pt-3">
            <span className="text-[10px] text-gray-400 font-medium block mb-1">{t('adr.relation.label')}</span>
            <AdrRelationPicker
              adrId={form.loadedAdrId}
              relations={form.existingAdr?.relations ?? []}
              onAdd={(relation) => actions.addRelation(form.loadedAdrId!, relation)}
              onRemove={(targetAdrId, type) => actions.removeRelation(form.loadedAdrId!, targetAdrId, type)}
            />
          </div>
        )}

        {/* People */}
        <div className="border-t border-gray-100 pt-3">
          <span className="text-[10px] text-gray-400 font-medium block mb-1">{t('adr.person.label')}</span>
          <AdrPersonPicker people={form.people} onChange={form.setPeople} />
        </div>

        {/* Comments */}
        <div className="border-t border-gray-100 pt-3">
          <span className="text-[10px] text-gray-400 font-medium block mb-1">{t('adr.comment.label')}</span>
          <AdrCommentTree
            comments={form.existingAdr?.comments ?? []}
            onAddComment={form.handleAddComment}
            onResolve={form.handleResolveComment}
            onUnresolve={form.handleUnresolveComment}
          />
        </div>
      </div>
    </>
  );
}
